import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Generic form dialog for the admin panel.
 *
 * Props:
 *  open, onOpenChange
 *  title, description
 *  fields: [{ name, label, type: "text"|"textarea", placeholder?, required? }]
 *  initialValues: { [name]: value }
 *  submitLabel
 *  onSubmit(values) — should throw to keep dialog open on error
 */
export default function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  fields = [],
  initialValues = {},
  submitLabel = "Save",
  onSubmit,
  testIdPrefix = "form",
}) {
  const [values, setValues] = useState(initialValues);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setValues(initialValues);
     
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (_) {
      // toast handled by caller
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white text-[#111827] rounded-xl"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              {title}
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="text-xs uppercase tracking-[0.15em] text-gray-500">
                {f.label}
                {f.required && <span className="text-[var(--terracotta)] ml-1">*</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  rows={4}
                  required={f.required}
                  placeholder={f.placeholder}
                  value={values[f.name] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [f.name]: e.target.value })
                  }
                  data-testid={`${testIdPrefix}-field-${f.name}`}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[var(--terracotta)] text-sm"
                />
              ) : (
                <input
                  type={f.type || "text"}
                  required={f.required}
                  placeholder={f.placeholder}
                  value={values[f.name] ?? ""}
                  onChange={(e) =>
                    setValues({ ...values, [f.name]: e.target.value })
                  }
                  data-testid={`${testIdPrefix}-field-${f.name}`}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[var(--terracotta)] text-sm"
                />
              )}
            </div>
          ))}
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid={`${testIdPrefix}-cancel`}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid={`${testIdPrefix}-submit`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--terracotta)] hover:bg-[var(--terracotta-hover)] text-white text-sm font-medium disabled:opacity-60"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {submitLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
